import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { ConversationList } from "@/components/inbox/conversation-list";
import { ConversationThread } from "@/components/inbox/conversation-thread";
import { ContactPanel } from "@/components/inbox/contact-panel";
import { ArrowLeft } from "lucide-react";
import { useInboxRealtime } from "@/hooks/use-inbox-realtime";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  listOrganizationMembers,
  type OrganizationMemberRecord,
} from "@/lib/organization-client";

type InboxFilters = {
  status?: string;
  channel?: string;
  assignee?: string;
  tag?: string;
  search?: string;
};

/**
 * Unified inbox page — conversation list, thread, and contact panel.
 * Reply across channels (A1), internal notes (A2), assignment (A3),
 * read/unread + status (A4), search (A5), quick replies (A7),
 * canned responses via /command (A8), tags (A9).
 * Realtime refresh via notification envelopes (agent-side WebSocket).
 * Keyboard: up/down=nav, R=reply, N=note, E=close, A=assign, /=quick
 */
export default function InboxPage() {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [showContactPanel, setShowContactPanel] = useState(true);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filters, setFilters] = useState<InboxFilters>(() => buildInitialInboxFilters());
  const [assignmentOptions, setAssignmentOptions] = useState<InboxAssignmentOption[]>([]);
  const [assignmentOptionsLoading, setAssignmentOptionsLoading] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);
  const [optimisticReadConversationIds, setOptimisticReadConversationIds] = useState<string[]>([]);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isMobileThreadOpen = isMobile && !!selectedConversationId;

  const performInboxAction = useCallback(async (
    path: string,
    init: RequestInit,
    errorMessage: string,
  ): Promise<boolean> => {
    try {
      await apiFetch(path, init);
      setActionError(null);
      return true;
    } catch {
      setActionError(errorMessage);
      return false;
    }
  }, []);

  // A4: Mark as read when selecting a conversation
  const handleSelectConversation = useCallback((id: string) => {
    setSelectedConversationId(id);
    setOptimisticReadConversationIds((current) => current.includes(id) ? current : [...current, id]);
    void performInboxAction(
      `/v1/inbox/conversations/${id}/read`,
      { method: "POST" },
      "Could not mark the conversation as read.",
    );
  }, [performInboxAction]);

  const handleArchiveConversation = useCallback(async (conversationId: string) => {
    const ok = await performInboxAction(`/v1/inbox/conversations/${conversationId}/status`, {
      method: "POST",
      body: JSON.stringify({ status: "closed" }),
    }, "Could not close the conversation.");
    if (!ok) return;
    setListRefreshKey((current) => current + 1);
    if (selectedConversationId === conversationId) {
      setSelectedConversationId(null);
      setThreadRefreshKey((current) => current + 1);
    }
  }, [performInboxAction, selectedConversationId]);

  const { isConnected } = useInboxRealtime((envelope) => {
    const conversationId = typeof envelope.payload?.conversationId === "string"
      ? envelope.payload.conversationId
      : null;

    if (conversationId && envelope.type === "notification.inbound_message") {
      setOptimisticReadConversationIds((current) => current.filter((entry) => entry !== conversationId));
      setListRefreshKey((current) => current + 1);
      if (conversationId === selectedConversationId) {
        setThreadRefreshKey((current) => current + 1);
      }
      return;
    }

    if (envelope.type === "inbox.conversation.updated") {
      setListRefreshKey((current) => current + 1);
      if (conversationId && conversationId === selectedConversationId) {
        setThreadRefreshKey((current) => current + 1);
      }
    }
  });

  // Focus assign input when toggled visible
  useEffect(() => {
    if (!showAssignPicker) {
      return;
    }

    let isCancelled = false;

    async function loadAssignmentOptions() {
      setAssignmentOptionsLoading(true);
      try {
        const [membersResponse, workloadResponse] = await Promise.all([
          listOrganizationMembers(),
          apiFetch<{ data: AssignmentWorkload[] }>("/v1/inbox/assignment-options"),
        ]);

        if (isCancelled) {
          return;
        }

        const workloadByAssignee = new Map(
          workloadResponse.data.map((entry) => [entry.assigneeId, entry]),
        );

        setAssignmentOptions(
          membersResponse.map((member) => {
            const workload = workloadByAssignee.get(member.id);
            return {
              id: member.id,
              name: member.name ?? member.email,
              email: member.email,
              role: member.role,
              image: member.image,
              availability: workload?.availability ?? "idle",
              openConversationCount: workload?.openConversationCount ?? 0,
            };
          }),
        );
      } catch {
        if (!isCancelled) {
          setActionError("Could not load team assignment options.");
          setAssignmentOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setAssignmentOptionsLoading(false);
        }
      }
    }

    void loadAssignmentOptions();

    return () => {
      isCancelled = true;
    };
  }, [showAssignPicker]);

  const handleAssignSubmit = useCallback(async (assignee: string | null) => {
    if (!selectedConversationId) return;
    const ok = await performInboxAction(`/v1/inbox/conversations/${selectedConversationId}/assign`, {
      method: "POST",
      body: JSON.stringify({ assignee }),
    }, "Could not assign the conversation.");
    if (!ok) return;
    setShowAssignPicker(false);
    setListRefreshKey((current) => current + 1);
  }, [performInboxAction, selectedConversationId]);

  // A4: Status management keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      if (!selectedConversationId) return;

      if (e.key === "e" || e.key === "E") {
        void performInboxAction(`/v1/inbox/conversations/${selectedConversationId}/status`, {
          method: "POST",
          body: JSON.stringify({ status: "closed" }),
        }, "Could not close the conversation.");
        setListRefreshKey((current) => current + 1);
        e.preventDefault();
      }
      // R key: only handle reopen in list view (no conversation selected handled above).
      // When a conversation is selected, let the child ConversationThread handle R for reply.
      if ((e.key === "a" || e.key === "A") && !showAssignPicker) {
        setShowAssignPicker(true);
        e.preventDefault();
      }

      if (e.key === "Escape" && showAssignPicker) {
        setShowAssignPicker(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [performInboxAction, selectedConversationId, showAssignPicker]);

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Left: Conversation List — hidden on mobile when a thread is open */}
      <div className={`${isMobileThreadOpen ? "hidden" : ""} w-80 flex-shrink-0 border-r border-[var(--color-border)] dark:border-[var(--color-border)] overflow-y-auto bg-[var(--color-card)] dark:bg-[var(--color-card)] md:block`}>
        <ConversationList
          selectedId={selectedConversationId}
          onSelect={handleSelectConversation}
          onArchive={handleArchiveConversation}
          filters={filters}
          onFiltersChange={setFilters}
          refreshKey={listRefreshKey}
          optimisticReadConversationIds={optimisticReadConversationIds}
          enableMobileSwipe={isMobile}
        />
      </div>

      {/* Center: Thread */}
      <div className={`${isMobile && !selectedConversationId ? "hidden" : ""} flex-1 flex flex-col overflow-hidden`}>
        {selectedConversationId ? (
          <>
            {!isConnected && (
              <div className="border-b border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-4 py-2 text-xs text-[var(--color-warning)]">
                Realtime inbox sync is reconnecting. Manual actions still work.
              </div>
            )}
            {actionError && (
              <div className="border-b border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-4 py-2 text-sm text-[var(--color-warning)]">
                {actionError}
              </div>
            )}
            {/* Mobile back button */}
            {isMobile && (
              <button
                onClick={() => setSelectedConversationId(null)}
                className="flex items-center gap-1 px-4 py-2 text-sm text-[var(--color-primary)] border-b border-[var(--color-border)] dark:border-[var(--color-border)] md:hidden"
              >
                <ArrowLeft className="w-4 h-4" /> Back to inbox
              </button>
            )}
            {/* Inline assignment picker — toggled by A key */}
            {showAssignPicker && (
              <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Assign conversation</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Status reflects current open inbox load for each teammate.</p>
                  </div>
                  <button
                    onClick={() => setShowAssignPicker(false)}
                    className="rounded px-2 py-1 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
                  >
                    Cancel
                  </button>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleAssignSubmit(null)}
                    className="flex items-center justify-between rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-left transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5"
                  >
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">Unassigned</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">Return this conversation to the shared queue.</p>
                    </div>
                  </button>
                  {assignmentOptionsLoading ? (
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
                      Loading team members...
                    </div>
                  ) : (
                    assignmentOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleAssignSubmit(option.id)}
                        className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-left transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          {option.image ? (
                            <img src={option.image} alt="" className="h-9 w-9 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-sm font-semibold text-[var(--color-primary)]">
                              {option.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{option.name}</p>
                            <p className="truncate text-xs text-[var(--color-muted-foreground)]">{option.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${AVAILABILITY_STYLES[option.availability]}`}>
                            {AVAILABILITY_LABELS[option.availability]}
                          </span>
                          <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
                            {option.openConversationCount} open
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {!assignmentOptionsLoading && assignmentOptions.length === 0 && (
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    No team members are available yet. Invite someone in Settings → Team.
                  </p>
                )}
              </div>
            )}
            <div className="flex min-h-0 flex-1 flex-col">
              <ConversationThread
                conversationId={selectedConversationId}
                onToggleContactPanel={() => setShowContactPanel(!showContactPanel)}
                refreshToken={threadRefreshKey}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[var(--color-muted-foreground)]">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-[var(--color-muted-foreground)]/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="mt-2 text-sm">Select a conversation</p>
              <p className="text-xs text-[var(--color-muted-foreground)]/70 mt-1">↑↓ to navigate, R to reply, N for note</p>
            </div>
          </div>
        )}
      </div>

      {/* Right: Contact Panel — hidden on mobile */}
      {showContactPanel && selectedConversationId && !isMobile && (
        <div className="w-80 flex-shrink-0 border-l border-[var(--color-border)] dark:border-[var(--color-border)] overflow-y-auto bg-[var(--color-card)] dark:bg-[var(--color-card)]">
          <ContactPanel conversationId={selectedConversationId} />
        </div>
      )}
    </div>
  );
}

type AssignmentAvailability = "idle" | "active" | "busy";

interface AssignmentWorkload {
  assigneeId: string;
  openConversationCount: number;
  availability: AssignmentAvailability;
}

type TeamMember = OrganizationMemberRecord;

interface InboxAssignmentOption {
  id: string;
  name: string;
  email: string;
  role: string;
  image: string | null;
  availability: AssignmentAvailability;
  openConversationCount: number;
}

const AVAILABILITY_LABELS: Record<AssignmentAvailability, string> = {
  idle: "Idle",
  active: "Active",
  busy: "Busy",
};

const AVAILABILITY_STYLES: Record<AssignmentAvailability, string> = {
  idle: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  active: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  busy: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
};

function buildInitialInboxFilters(): InboxFilters {
  const initialSearch = readInboxSearchParam();
  return initialSearch ? { status: "open", search: initialSearch } : { status: "open" };
}

function readInboxSearchParam(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const value = new URLSearchParams(window.location.search).get("search")?.trim();
  return value || undefined;
}
